import { useEffect } from 'react';

export default function WhatsNewDialog({ isOpen, onClose }) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const features = [
{
    version: "v2.0.3",
    date: "November 5, 2025",
    title: "PostCSS Support",
    description: "Added comprehensive PostCSS integration for advanced CSS processing in modern build workflows.",
    type: "feature",
    isLatest: true,
    link: "/#/installation",
    items: [
      "PostCSS plugin support for advanced processing",
      "Works seamlessly with Vite and other build tools",
      "Improved compatibility with modern CSS features"
    ]
  },
  {
    version: "v2.0.0",
    date: "November 4, 2025",
    title: "Vite Integration",
    description: "Native Vite plugin integration for fast development and optimized builds.",
    type: "feature",
    link: "/#/installation",
    items: [
      "Native Vite plugin integration",
      "Hot Module Replacement (HMR) compatibility",
      "Optimized build performance with Vite",
      "Seamless integration with modern JavaScript frameworks"
    ]
  },
    {
    version: "v1.2.17",
    date: "October 16, 2025",
    title: "CDN Support Added",
    description: "Introduced CDN support for easy integration without build tools or npm installation.",
    type: "feature",
    link: "/#/installation",
    items: [
      "Direct CDN link integration via unpkg",
      "No build process required for quick prototyping",
      "Instant access to all Unicss utilities",
      "Perfect for CodePen, JSFiddle, and rapid development",
      "Simplified setup for beginners and quick demos"
    ]
    
  },
    {
  version: "v1.2.16",
  date: "October 15, 2025",
  title: "Font Family Utility Updates",
  description: "Updated font family utilities with more descriptive naming for better clarity and consistency.",
  type: "feature",
  link: "/#/font-family",
  items: [
    "font-sans renamed to font-family-sans",
    "font-serif renamed to font-family-serif", 
    "font-mono renamed to font-family-mono",
    "font-[<value>] renamed to font-family-[<value>]"
  ]
},
    {
  version: "v1.2.12",
  date: "October 14, 2025",
  title: "Pseudo-Class Support",
  description: "Added comprehensive pseudo-class support for interactive states and enhanced styling capabilities.",
  type: "feature",
  link: "/#/hover-focus-and-other-states",
  items: [
    "Hover, focus, and active state utilities",
    "Form state pseudo-classes (valid, invalid, required)",
    "Structural pseudo-classes (first-child, last-child, nth-child)",
    "Interactive pseudo-classes (disabled, checked, selected)",
    "Enhanced user experience with state-based styling"
  ]
},
{
    version: "v1.2.11",
    date: "October 10, 2025",
    title: "Will-Change Property Support",
    description: "Added will-change utilities to optimize rendering performance for animations and transitions.",
    type: "feature",
    link: "/#/will-change",
    items: [
      "will-change-auto for automatic optimization",
      "will-change-scroll for scroll-based animations",
      "will-change-contents for content changes",
      "will-change-transform for transform animations",
      "Performance optimization for smooth animations"
    ]
  },
    {
      version: "v1.1.0",
      date: "September 10, 2025",
      title: "Dark Mode Support",
      description: "Complete dark mode implementation with system preference detection.",
      type: "feature",
      link: "/#/dark-mode",
      items: [
        "System preference auto-detection",
        "Persistent theme selection",
        "Smooth theme transitions",
        "Dark mode optimized color palette"
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-99 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-color-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-color-white dark:bg-color-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between lg:p-6 p-4 border-b border-color-gray-200 dark:border-color-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-color-blue-100 dark:bg-color-blue-900/30 rounded-full">
                <svg className="w-5 h-5 text-color-blue-600 dark:text-color-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-color-gray-900 dark:text-color-white">
                  What's New
                </h2>
                <p className="text-sm text-color-gray-500 dark:text-color-gray-400">
                  Latest updates and features
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full cursor-pointer hover:bg-color-gray-100 dark:hover:bg-color-gray-700 transition-colors"
              aria-label="Close dialog"
            >
              <svg className="w-5 h-5 text-color-gray-500 dark:text-color-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="lg:p-6 p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className={`relative ${feature.isLatest ? '' : ''}`}>
                  
                  
                  {/* Timeline line */}
                  {index !== features.length - 1 && (
                    <div className="absolute left-4 top-12 bottom-0 w-px bg-color-gray-200 dark:bg-color-gray-700" />
                  )}
                  
                  <div className="flex items-start space-x-4">
                    {/* Version badge */}
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold ${
                      feature.isLatest 
                        ? 'bg-color-purple-100 text-color-white shadow-lg'
                        : 'bg-color-yellow-50 dark:bg-color-yellow-900/30 text-yellow-700 dark:text-color-yellow-400'
                    }`}>
                      {feature.isLatest ? '🎉' : '✨'}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="lg:flex items-center justify-between mb-2">
                        <h3 className={`text-lg font-semibold ${
                          feature.isLatest 
                            ? 'text-color-purple-700 dark:text-color-blue-300' 
                            : 'text-color-gray-900 dark:text-color-white'
                        }`}>
                          {feature.title}
                          {/* {feature.isLatest && (
                            <span className="ml-2 text-sm bg-gradient-to-r from-color-blue-500 to-color-purple-500 bg-clip-text text-transparent font-bold">
                              NEW
                            </span>
                          )} */}
                        </h3>
                        <div className="flex relative items-center space-x-2 text-xs text-color-gray-500 dark:text-color-gray-400">
                          <span className={`font-mono px-2 py-1 rounded ${
                            feature.isLatest 
                              ? 'bg-color-purple-100 text-color-purple-700'
                              : 'bg-color-gray-100 dark:bg-color-gray-700'
                          }`}>
                            {feature.version}
                          </span>
                          <span>{feature.date}</span>
                          {feature.isLatest && (
                              <div className="absolute animate-pulse top-2 -right-2 bg-color-red-500 text-color-white text-xs font-semibold px-1 py-1 rounded-full shadow-lg">
                              </div>
                            )}
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p className="text-sm text-color-gray-700 dark:text-color-gray-300 mb-3">
                        {feature.description}
                      </p>
                      
                      {/* Feature list */}
                      <ul className="space-y-2">
                        {feature.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start space-x-2 text-sm text-color-gray-600 dark:text-color-gray-300">
                            <svg className="w-4 h-4 text-color-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <a onClick={onClose} href={feature.link} className="text-color-blue-500 hover:bg-color-blue-100 rounded-md bg-color-blue-50 w-fit text-sm px-2 py-1 mt-4 block no-underline dark:bg-color-blue-100/50 dark:text-color-white">View More</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}